import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store/store";

Vue.use(VueRouter);

const navigationIds = new WeakMap();
let latestNavigationId = 0;

// A new tab has no roles until the role lookup answers, so wait for it, then
// let the user in only when the getter is true.
function requireRoleWhenResolved(allowedGetter, to, from, next) {
  const navId = navigationIds.get(to);
  const stillCurrent = () => navId == null || navId === latestNavigationId;
  const decide = () => {
    if (!stillCurrent()) {
      next();
      return;
    }
    if (!store.getters[allowedGetter]) {
      next({ name: "NBN Direct" });
      return;
    }
    next();
  };

  if (store.getters["auth/rolesResolved"]) {
    decide();
    return;
  }

  const unwatch = store.watch(
    () => store.getters["auth/rolesResolved"],
    resolved => {
      if (!resolved) return;
      unwatch();
      decide();
    }
  );
}

function requireBulkUploadAdmin(to, from, next) {
  requireRoleWhenResolved("auth/isBulkUploadAdmin", to, from, next);
}

function requireConnectAdmin(to, from, next) {
  requireRoleWhenResolved("auth/isAdmin", to, from, next);
}

function requireConnectSuperAdmin(to, from, next) {
  if (!store.getters["auth/isSuperAdmin"]) {
    next({ name: "NBN Direct" });
    return;
  }
  next();
}

function loadView(view) {
  return () =>
    import(/* webpackChunkName: "view-[request]" */ `@/views/${view}.vue`);
}

const routes = [
  {
    path: "/orders",
    name: "Orders",
    component: loadView("OrderManagement")
  },
  {
    path: "/nbn-orders",
    name: "Nbn Orders",
    component: loadView("NbnOrderManagement")
  },
  {
    path: "/nbn-active-orders",
    name: "Nbn Active Orders",
    component: loadView("NbnActiveOrderManagement")
  },
  {
    path: "/",
    name: "NBN Direct",
    component: loadView("NbnOrderManagement")
  },
  {
    path: "/nbn-bulk-upload",
    name: "Nbn Bulk Upload",
    component: loadView("NbnBulkUpload"),
    beforeEnter: requireBulkUploadAdmin
  },
  {
    path: "/appointments",
    name: "Appointments",
    component: loadView("AppointmentManagement")
  },
  {
    path: "/excluded-products",
    name: "Excluded Products",
    component: loadView("ExcludedProducts")
  },
  {
    path: "/service-qualification",
    name: "Service Qualification",
    component: loadView("ServiceQualification")
  },
  {
    path: "/order-qualification",
    name: "Order Qualification",
    component: loadView("OrderQualification")
  },
  {
    //TO DO: remove feature flag
    path: "/service-qualification-5g",
    name: "* Service Qualification",
    component: loadView("ServiceQualification5G")
  },
  {
    path: "/service-qualification-nbn",
    name: "Nbn Service Qualification",
    component: loadView("ServiceQualificationNbn")
  },
  {
    path: "/nbn-appointment-management",
    name: "Nbn Appointment Management",
    component: loadView("NbnAppointmentManagement")
  },
  {
    path: "/nbn-ticket-management",
    name: "Nbn Ticket Management",
    component: loadView("NbnTicketManagement")
  },
  {
    path: "/organisation-management",
    name: "Nbn Organisation Management",
    component: loadView("OrganisationManagement")
  },
  {
    path: "/quote-management",
    name: "Nbn Quote Management",
    component: loadView("QuoteManagement")
  },
  {
    path: "/auth",
    name: "Auth",
    hidden: true,
    meta: { title: "auth callback", requiresAuth: false },
    component: loadView("Auth")
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    hidden: true,
    meta: { title: "unauthorised", requiresAuth: false },
    component: loadView("Unauthorized")
  },
  {
    path: "/migration-result",
    name: "Migration Result",
    component: loadView("MigrationResult")
  },
  {
    path: "/banner-announcement",
    name: "Banner Annoucement",
    component: loadView("BannerAnnouncement")
  },
  {
    path: "/audit-logs",
    name: "Audit Logs",
    component: loadView("AuditLogs")
  },
  {
    path: "/cvc-migration",
    name: "CVC Migration",
    component: loadView("CvcMigration"),
    beforeEnter: requireConnectAdmin
  },
  {
    path: "/nbn-order-correction",
    name: "NBN Order Correction",
    component: loadView("NbnOrderCorrection"),
    beforeEnter: requireConnectSuperAdmin
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

router.beforeEach((to, from, next) => {
  latestNavigationId += 1;
  navigationIds.set(to, latestNavigationId);
  next();
});

export default router;
